/* Case-study media helpers: cover crop/position modal + gallery manager. */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, Film, ChevronUp, ChevronDown, ExternalLink, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CoverCropModal({ file, slug, headers, onDone, onCancel }) {
    const FRAME_W = 320, FRAME_H = 400;               // 4:5 — matches /work cards
    const [img, setImg] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const [busy, setBusy] = useState(false);
    const drag = useRef(null);

    useEffect(() => {
        const url = URL.createObjectURL(file);
        const el = new Image();
        el.onload = () => setImg(el);
        el.src = url;
        return () => URL.revokeObjectURL(url);
    }, [file]);

    if (!img) return null;
    const baseScale = Math.max(FRAME_W / img.width, FRAME_H / img.height);
    const scale = baseScale * zoom;
    const dw = img.width * scale, dh = img.height * scale;
    const clamp = (p) => ({
        x: Math.min((dw - FRAME_W) / 2, Math.max(-(dw - FRAME_W) / 2, p.x)),
        y: Math.min((dh - FRAME_H) / 2, Math.max(-(dh - FRAME_H) / 2, p.y)),
    });
    const start = (e) => { drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y }; };
    const move = (e) => {
        if (!drag.current) return;
        setPos(clamp({ x: drag.current.ox + e.clientX - drag.current.sx, y: drag.current.oy + e.clientY - drag.current.sy }));
    };
    const end = () => { drag.current = null; };

    const save = async () => {
        setBusy(true);
        try {
            const k = 1200 / FRAME_W;
            const canvas = document.createElement("canvas");
            canvas.width = 1200; canvas.height = 1500;
            const ctx = canvas.getContext("2d");
            const left = FRAME_W / 2 - dw / 2 + pos.x;
            const top = FRAME_H / 2 - dh / 2 + pos.y;
            ctx.drawImage(img, left * k, top * k, dw * k, dh * k);
            const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.9));
            const fd = new FormData();
            fd.append("file", new File([blob], "cover.jpg", { type: "image/jpeg" }));
            await axios.post(`${API}/projects/${slug}/hero-upload`, fd, { headers });
            toast.success("封面已裁剪并上传");
            onDone();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="cover-crop-modal">
            <div className="bg-white rounded-2xl p-5 w-full max-w-md select-none">
                <div className="font-medium mb-1">调整封面 · 拖动定位,滑块缩放</div>
                <div className="text-xs text-neutral-500 mb-3">框内即公开页显示的范围 (4:5)</div>
                <div className="mx-auto overflow-hidden rounded-xl border border-neutral-200 relative cursor-grab active:cursor-grabbing"
                    style={{ width: FRAME_W, height: FRAME_H, touchAction: "none" }}
                    onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); start(e); }}
                    onPointerMove={move} onPointerUp={end} onPointerLeave={end} data-testid="crop-frame">
                    <img src={img.src} alt="" draggable={false} className="absolute max-w-none pointer-events-none"
                        style={{ width: dw, height: dh, left: FRAME_W / 2 - dw / 2 + pos.x, top: FRAME_H / 2 - dh / 2 + pos.y }} />
                </div>
                <div className="flex items-center gap-3 mt-4">
                    <span className="text-xs text-neutral-500 w-8">缩放</span>
                    <input type="range" min="1" max="3" step="0.01" value={zoom} data-testid="crop-zoom"
                        onChange={(e) => { setZoom(parseFloat(e.target.value)); setPos((p) => clamp(p)); }} className="flex-1" />
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onCancel} className="px-4 py-2 rounded-full border text-sm" data-testid="crop-cancel">取消</button>
                    <button onClick={save} disabled={busy} data-testid="crop-save"
                        className="px-5 py-2 rounded-full bg-cafe-olive text-cafe-cream text-sm disabled:opacity-50">
                        {busy ? "上传中…" : "确认并上传"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export function GalleryModal({ project, headers, onClose, onChanged }) {
    const [items, setItems] = useState(project.gallery || []);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    const addFiles = async (e) => {
        const files = [...(e.target.files || [])];
        e.target.value = "";
        if (!files.length) return;
        setBusy(true);
        let g = items;
        for (const f of files) {
            try {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await axios.post(`${API}/projects/${project.slug}/gallery-upload`, fd, { headers });
                g = data.gallery;
                setItems(g);
            } catch (err) {
                toast.error(`${f.name}: ${err?.response?.data?.detail || "upload failed"}`);
            }
        }
        setBusy(false); onChanged();
        toast.success(`相册已更新 (${g.length} 张)`);
    };

    const remove = async (url) => {
        const next = items.filter((u) => u !== url);
        try {
            await axios.patch(`${API}/projects/${project.slug}`, { gallery: next }, { headers });
            setItems(next); onChanged();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };

    const moveItem = async (idx, delta) => {
        const next = [...items];
        const target = idx + delta;
        if (target < 0 || target >= next.length) return;
        [next[idx], next[target]] = [next[target], next[idx]];
        setItems(next);
        try {
            await axios.put(`${API}/projects/${project.slug}/gallery-order`,
                            { gallery: next }, { headers });
            onChanged();
        } catch (err) {
            setItems(items);  // revert optimistic update
            toast.error(err?.response?.data?.detail || "重新排序失败");
        }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="gallery-modal">
            <div className="bg-white rounded-2xl p-5 w-full max-w-2xl max-h-[85vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="font-medium">案例相册 · {project.title}</div>
                        <div className="text-xs text-neutral-500">{items.length}/24 张 — 公开案例页会显示这些照片</div>
                    </div>
                    <button onClick={onClose} data-testid="gallery-close" className="p-2 rounded-full hover:bg-neutral-100"><X className="h-4 w-4" /></button>
                </div>
                <input type="file" accept="image/*" multiple ref={inputRef} onChange={addFiles} className="hidden" data-testid="gallery-file-input" />
                <button onClick={() => inputRef.current?.click()} disabled={busy || items.length >= 24} data-testid="gallery-add-btn"
                    className="w-full border-2 border-dashed border-neutral-300 rounded-xl py-6 text-sm text-neutral-500 hover:border-cafe-olive hover:text-cafe-olive transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    <Upload className="h-4 w-4" />{busy ? "上传中…" : "点击选择照片(可多选,每张 ≤8MB)"}
                </button>
                {items.length > 0 && (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-4">
                        {items.map((u, i) => (
                            <div key={u} className="relative group aspect-square rounded-lg overflow-hidden bg-neutral-100" data-testid={`gallery-item-${i}`}>
                                <img src={u} alt="" className="w-full h-full object-cover" />
                                <div className="absolute inset-x-1 top-1 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="flex gap-1">
                                        <button onClick={() => moveItem(i, -1)} disabled={i === 0} data-testid={`gallery-up-${i}`}
                                            className="bg-black/60 text-white rounded-full p-1 disabled:opacity-30"
                                            aria-label="向前移"><ChevronUp className="h-3 w-3" /></button>
                                        <button onClick={() => moveItem(i, 1)} disabled={i === items.length - 1} data-testid={`gallery-down-${i}`}
                                            className="bg-black/60 text-white rounded-full p-1 disabled:opacity-30"
                                            aria-label="向后移"><ChevronDown className="h-3 w-3" /></button>
                                    </div>
                                    <button onClick={() => remove(u)} data-testid={`gallery-remove-${i}`}
                                        className="bg-black/60 text-white rounded-full p-1" aria-label="移除">
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                                <div className="absolute bottom-1 left-1 text-[10px] text-white bg-black/60 rounded px-1.5 py-0.5">{i + 1}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}


const CHUNK_SIZE = 5 * 1024 * 1024;
const VIDEO_MAX_MB = 200;
const POSTER_MAX_MB = 8;
const VIDEO_MIME = { "video/mp4": "mp4", "video/quicktime": "mov", "video/webm": "webm" };

/* ------------------------------------------------------------------ *
 * directR2Upload — the ONLY upload path for Case Study hero videos.
 *
 * 2026-02 rewrite: previously this function fell back to a chunked
 * proxy upload (/video-upload-init + /video-upload-chunk + /video-upload-finalize)
 * whenever the presigned PUT failed. That fallback was architecturally
 * broken because every chunk hit Cloudflare's 100-second request
 * timeout, so large (>60 MB) uploads returned HTTP 502. We now:
 *   1. Call /video-upload-presign to get a short-lived R2 PUT URL.
 *   2. XHR PUT the file bytes DIRECTLY to R2 (origin ≠ our app host).
 *   3. On any failure, surface a *typed* error:
 *        - PRESIGN_FAILED : backend couldn't sign the URL (unlikely)
 *        - CORS_BLOCKED   : xhr.status === 0 → R2 bucket CORS does not
 *          allow this origin. Caller shows the exact CORS JSON to paste.
 *        - NETWORK        : xhr.onerror with a browser error.
 *        - R2_HTTP        : R2 returned a 4xx/5xx (AccessDenied, etc.).
 *        - CANCELLED      : user clicked cancel.
 * The caller then classifies and shows the right remediation (no
 * silent fall-back onto a broken path).
 * ------------------------------------------------------------------ */
async function directR2Upload(file, { headers, project, abortRef, onStage, onProgress }) {
    // Step 1 — sign the upload URL. Short timeout: our backend presign is a
    // few lines of boto3, if it takes longer than 15 s something is wrong.
    onStage?.("signing");
    let presign;
    try {
        const r = await axios.post(
            `${API}/projects/${project.slug}/video-upload-presign`,
            { content_type: file.type, size: file.size, filename: file.name },
            { headers, timeout: 15000 });
        presign = r.data;
    } catch (e) {
        const err = new Error(
            e?.code === "ECONNABORTED"
                ? "签名请求 15 秒超时 — 后端服务器未响应。请再试一次。"
                : (e?.response?.data?.detail?.message
                   || e?.response?.data?.detail
                   || e?.message
                   || "Could not sign upload URL")
        );
        err.kind = "PRESIGN_FAILED";
        err.httpStatus = e?.response?.status;
        throw err;
    }

    // Step 2 — direct PUT to R2. Three watchdogs below protect against the
    // three failure modes we've seen in production:
    //   (a) CORS preflight silently stalls (no CORS headers from R2).
    //   (b) PUT accepted but first upload-progress event never fires because
    //       the first chunk round-trip is slow on the user's uplink.
    //   (c) PUT started but stalls mid-file (connection reset).
    onStage?.("uploading");
    await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        let lastProgressAt = Date.now();
        let firstByteSeen = false;

        // (a) 15-second watchdog for the FIRST byte going on the wire.
        //     If the browser is still sitting on the CORS preflight, this
        //     fires and tells the user exactly what's wrong rather than
        //     leaving them staring at 0%.
        const preflightTimer = setTimeout(() => {
            if (!firstByteSeen) {
                try { xhr.abort(); } catch (_) {}
                const err = new Error(
                    "15 秒内 R2 没有响应首个数据包。几乎总是因为 R2 bucket CORS 没允许 PUT,或 R2 API token 缺 Object Write 权限。"
                );
                err.kind = "CORS_BLOCKED";
                reject(err);
            }
        }, 15000);

        // (c) 60-second stall watchdog — once bytes start flowing, if we go
        //     60 s without any new progress event, abort.
        const stallTimer = setInterval(() => {
            if (firstByteSeen && Date.now() - lastProgressAt > 60000) {
                try { xhr.abort(); } catch (_) {}
                const err = new Error("R2 上传中断 60 秒无进度 — 网络连接可能已断开。原始文件未保存,请重试。");
                err.kind = "NETWORK";
                reject(err);
            }
        }, 5000);

        const cleanup = () => { clearTimeout(preflightTimer); clearInterval(stallTimer); };

        xhr.open("PUT", presign.upload_url);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.upload.onprogress = (ev) => {
            if (!firstByteSeen) firstByteSeen = true;
            lastProgressAt = Date.now();
            if (ev.lengthComputable && onProgress) {
                onProgress({ loaded: ev.loaded, total: ev.total, fraction: ev.loaded / ev.total });
            }
        };
        xhr.onload = () => {
            cleanup();
            if (xhr.status >= 200 && xhr.status < 300) return resolve();
            const err = new Error(`R2 PUT ${xhr.status}: ${(xhr.responseText || "").slice(0, 200) || "upload rejected"}`);
            err.kind = "R2_HTTP";
            err.httpStatus = xhr.status;
            reject(err);
        };
        xhr.onerror = () => {
            cleanup();
            // status === 0 nearly always means CORS blocked. But our 15s
            // preflight watchdog already fires with a specific message, so
            // if we end up here with status 0, it's a dropped network
            // *after* the first byte went out.
            const err = new Error(firstByteSeen
                ? "R2 PUT 连接中断"
                : "浏览器未能连上 R2 — 请检查网络和 CORS 配置");
            err.kind = xhr.status === 0 ? "CORS_BLOCKED" : "NETWORK";
            err.httpStatus = xhr.status;
            reject(err);
        };
        xhr.onabort = () => {
            cleanup();
            const err = new Error("已取消 / cancelled by user");
            err.kind = "CANCELLED";
            reject(err);
        };
        if (abortRef?.current?.current) { cleanup(); xhr.abort(); return; }
        if (abortRef?.current) abortRef.current.xhr = xhr;
        xhr.send(file);
    });
    return { key: presign.key };
}


function classifyUploadError(err) {
    /* Translate a raw error (axios response, upload error, server detail
       object, string) into { title, message, canRetryConfirmOnly, hint } */
    const kind = err?.kind;
    const rsp = err?.response;
    const body = rsp?.data;
    const detailObj = (body && typeof body === "object") ? body.detail : null;
    const detailCode = (detailObj && typeof detailObj === "object") ? detailObj.code : null;
    const detailMsg = (detailObj && typeof detailObj === "object") ? detailObj.message : null;
    const detailStr = typeof detailObj === "string" ? detailObj : null;
    const status = rsp?.status || err?.httpStatus;

    if (kind === "CORS_BLOCKED") {
        return {
            title: "R2 bucket CORS 未允许当前域名",
            message: "浏览器直接上传到 R2 被拦截。这是 Cloudflare R2 bucket CORS 配置的问题,不是服务器错误。打开 Cloudflare → R2 → 相应 bucket → Settings → CORS Policy,粘贴 Studio → 'Hero Film 上传诊断' 卡片提供的 JSON。配置后几秒生效,然后点击重试。",
            canRetryConfirmOnly: false,
            hint: "CORS",
            status: 0,
        };
    }
    if (kind === "R2_HTTP") {
        return {
            title: "R2 拒绝上传",
            message: `R2 返回 HTTP ${err.httpStatus}。常见原因:签名过期、权限不足或 Content-Type 不匹配。请点击重试;如果多次失败,把这个状态码截图发给我。`,
            canRetryConfirmOnly: false,
            hint: `R2 HTTP ${err.httpStatus}`,
            status: err.httpStatus,
        };
    }
    if (kind === "NETWORK") {
        return {
            title: "网络传输中断",
            message: "文件上传到 R2 的过程中网络断开。原始文件未保存。请检查网络后重试。",
            canRetryConfirmOnly: false,
            hint: "network",
        };
    }
    if (kind === "CANCELLED") {
        return { title: "已取消", message: "", canRetryConfirmOnly: false, hint: "cancelled" };
    }
    if (kind === "PRESIGN_FAILED") {
        return {
            title: "服务器无法签名上传地址",
            message: `${err.message || "Unknown"}${status ? " (HTTP " + status + ")" : ""}. 服务器可能无法连接 R2;请再试一次。`,
            canRetryConfirmOnly: false,
            hint: "presign",
        };
    }
    // Server-side confirm error with a {code, message} detail
    if (detailCode === "r2_object_missing") {
        return {
            title: "文件未抵达 R2",
            message: detailMsg || "浏览器上传看似成功,但 R2 上没有找到对象。几乎总是 R2 bucket CORS 未配置导致的静默失败。请打开 Studio → 'Hero Film 上传诊断' 查看 CORS 配置并修复。",
            canRetryConfirmOnly: false,
            hint: "CORS",
        };
    }
    if (detailCode === "r2_probe_failed" || detailCode === "r2_size_failed") {
        return {
            title: "无法连接 R2 验证上传",
            message: detailMsg || "服务器暂时无法连接 R2。请稍后点击「仅重新保存」(不需重传)。",
            canRetryConfirmOnly: true,
            hint: "r2",
        };
    }
    if (detailCode === "r2_empty_object") {
        return {
            title: "R2 上的文件为空",
            message: detailMsg || "文件上传失败。请重试完整上传。",
            canRetryConfirmOnly: false,
            hint: "empty",
        };
    }
    if (detailCode === "db_save_failed") {
        return {
            title: "文件已存储,但保存关联失败",
            message: detailMsg || "原片已安全存在 R2 上。请点击「仅重新保存」避免重复上传。",
            canRetryConfirmOnly: true,
            hint: "db",
        };
    }
    if (detailCode === "too_large") {
        return { title: "视频过大", message: detailMsg || "超过 200MB 上限。", canRetryConfirmOnly: false };
    }
    if (detailCode === "server_crash") {
        return {
            title: "服务器错误",
            message: detailMsg || "后端崩溃。请重试;持续失败请把 request ID 发给我。",
            canRetryConfirmOnly: true,
            hint: "server",
        };
    }
    // generic
    const text = detailStr || detailMsg || err?.message || "上传失败";
    return {
        title: "上传失败",
        message: `${text}${status ? ` (HTTP ${status})` : ""}`,
        canRetryConfirmOnly: false,
        hint: "generic",
    };
}


const PENDING_KEY = (slug) => `caseStudyUpload:pending:${slug}`;


export function VideoUploadModal({ project, headers, onClose, onChanged }) {
    const [file, setFile] = useState(null);
    const [posterFile, setPosterFile] = useState(null);
    // Progress model: separate percent (for the bar) + bytes (for the "x of
    // y MB" sub-label) + waiting flag (for the "等待 R2 响应首个数据包…"
    // state that fires while the preflight hasn't yielded any upload event
    // yet). This way the UI can show something more honest than
    // "上传中 · 0%" during the preflight / first-byte wait.
    const [progress, setProgress] = useState({ pct: 0, loaded: 0, total: 0, firstByte: false });
    const [stage, setStage] = useState("idle"); // idle|signing|uploading|confirming|processing|poster|done|error|cancelled
    const [errInfo, setErrInfo] = useState(null);
    const [externalUrl, setExternalUrl] = useState(project.video_url || "");
    const [uploadedPreview, setUploadedPreview] = useState(project.video_file_url || "");
    const [posterPreview, setPosterPreview] = useState(project.video_poster || "");
    const [pendingKey, setPendingKey] = useState(() => {
        // 2026-02 — if a previous upload reached R2 but failed to be confirmed,
        // keep the R2 key so a retry only re-runs confirm (no reupload).
        try { return localStorage.getItem(PENDING_KEY(project.slug)) || null; } catch { return null; }
    });
    const [playback, setPlayback] = useState({ status: "unknown", url: null });
    const abortRef = useRef({ current: false, xhr: null });

    // Poll playback status while processing.
    useEffect(() => {
        if (!uploadedPreview) return;
        let stopped = false;
        const tick = async () => {
            try {
                const r = await axios.get(`${API}/projects/${project.slug}/video-playback-status`, { headers });
                if (stopped) return;
                setPlayback({ status: r.data.status || "unknown", url: r.data.playback_url || null });
            } catch (_) {}
        };
        tick();
        const id = setInterval(tick, 5000);
        return () => { stopped = true; clearInterval(id); };
    }, [uploadedPreview, project.slug, headers]);

    const pickVideo = (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!VIDEO_MIME[f.type]) { toast.error("只支持 MP4 / MOV / WebM 视频"); return; }
        if (f.size > VIDEO_MAX_MB * 1024 * 1024) { toast.error(`视频过大 (最大 ${VIDEO_MAX_MB}MB)`); return; }
        setFile(f); setErrInfo(null);
        setProgress({ pct: 0, loaded: 0, total: f.size, firstByte: false });
        setStage("idle");
    };
    const pickPoster = (e) => {
        const f = e.target.files?.[0]; e.target.value = "";
        if (!f) return;
        if (!/^image\/(jpeg|png|webp)$/.test(f.type)) { toast.error("Poster 只支持 JPG / PNG / WebP"); return; }
        if (f.size > POSTER_MAX_MB * 1024 * 1024) { toast.error(`Poster 过大 (最大 ${POSTER_MAX_MB}MB)`); return; }
        setPosterFile(f);
    };

    const _runConfirm = async (key) => {
        setStage("confirming");
        // 30 s is generous — confirm is a Mongo write + an R2 head_object.
        // If it takes longer than that something is wrong on the backend.
        const confirm = await axios.post(
            `${API}/projects/${project.slug}/video-upload-confirm`,
            { key }, { headers, timeout: 30000 });
        try { localStorage.removeItem(PENDING_KEY(project.slug)); } catch (_) {}
        setPendingKey(null);
        setUploadedPreview(confirm.data.video_file_url);
    };

    const retryConfirmOnly = async () => {
        if (!pendingKey) return;
        setErrInfo(null);
        try {
            await _runConfirm(pendingKey);
            setStage("done");
            setProgress((p) => ({ ...p, pct: 100 }));
            toast.success("已重新保存视频关联");
            onChanged?.();
        } catch (err) {
            setErrInfo(classifyUploadError(err));
            setStage("error");
        }
    };

    const uploadVideo = async () => {
        if (!file) return;
        abortRef.current = { current: false, xhr: null };
        setErrInfo(null);
        setProgress({ pct: 0, loaded: 0, total: file.size, firstByte: false });
        try {
            const { key } = await directR2Upload(file, {
                headers, project, abortRef,
                onStage: setStage,
                onProgress: ({ loaded, total, fraction }) => {
                    setProgress({
                        pct: Math.round(fraction * 95),
                        loaded, total,
                        firstByte: true,
                    });
                },
            });
            try { localStorage.setItem(PENDING_KEY(project.slug), key); } catch (_) {}
            setPendingKey(key);
            setProgress((p) => ({ ...p, pct: 95, firstByte: true }));
            await _runConfirm(key);
            setProgress((p) => ({ ...p, pct: 100 }));
            if (posterFile) {
                setStage("poster");
                const pfd = new FormData(); pfd.append("file", posterFile);
                const pr = await axios.post(`${API}/projects/${project.slug}/video-poster-upload`, pfd, { headers, timeout: 60000 });
                setPosterPreview(pr.data.video_poster);
            }
            setStage("done");
            toast.success("视频已上传,后台正在生成网页播放版本");
            onChanged?.();
        } catch (err) {
            // CANCELLED is user-initiated — don't flag as error.
            if (err?.kind === "CANCELLED") {
                setStage("cancelled");
                return;
            }
            console.error("[case-study upload] failed:", err);
            setErrInfo(classifyUploadError(err));
            setStage("error");
        }
    };

    const retryTranscode = async () => {
        try {
            await axios.post(`${API}/projects/${project.slug}/video-retry-transcode`, {}, { headers });
            toast.success("已重新加入后台处理队列");
            setPlayback({ status: "processing", url: null });
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || err.message);
        }
    };

    const saveExternalOnly = async () => {
        try {
            await axios.patch(`${API}/projects/${project.slug}`, { video_url: externalUrl }, { headers });
            toast.success("外部视频链接已保存"); onChanged?.(); onClose();
        } catch (err) { toast.error(err?.response?.data?.detail || "保存失败"); }
    };
    const removeUploaded = async () => {
        if (!window.confirm("确定清除已上传的视频与 Poster?")) return;
        try {
            await axios.post(`${API}/projects/${project.slug}/video-remove`, {}, { headers });
            setUploadedPreview(""); setPosterPreview(""); setFile(null); setPosterFile(null);
            setStage("idle"); setProgress(0);
            try { localStorage.removeItem(PENDING_KEY(project.slug)); } catch (_) {}
            setPendingKey(null);
            toast.success("已清除上传视频"); onChanged?.();
        } catch (err) { toast.error(err?.response?.data?.detail || "清除失败"); }
    };
    const busy = stage === "signing" || stage === "uploading" || stage === "confirming" || stage === "poster";

    const uploadButtonLabel = (() => {
        if (stage === "signing") return "正在获取签名…";
        if (stage === "uploading" && !progress.firstByte) return "等待 R2 响应…";
        if (stage === "uploading") return `上传中 ${progress.pct}%`;
        if (stage === "confirming") return "确认中…";
        if (stage === "poster") return "上传 Poster 中…";
        if (stage === "error" || stage === "cancelled") return "重试上传";
        return "开始上传";
    })();

    return (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="video-modal">
            <div className="bg-white rounded-2xl p-5 w-full max-w-xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="font-medium flex items-center gap-2"><Film className="h-4 w-4" /> Hero Film · {project.title}</div>
                        <div className="text-xs text-neutral-500">直接上传 MP4 / MOV / WebM(≤{VIDEO_MAX_MB}MB) · Poster ≤{POSTER_MAX_MB}MB · 草稿私有保护</div>
                    </div>
                    <button onClick={onClose} disabled={busy} data-testid="video-close" className="p-2 rounded-full hover:bg-neutral-100 disabled:opacity-30"><X className="h-4 w-4" /></button>
                </div>
                {uploadedPreview && (
                    <div className="mb-4 p-3 bg-neutral-50 rounded-xl text-xs" data-testid="video-saved">
                        <div className="font-medium mb-1 flex items-center gap-2">
                            当前已保存的视频
                            {playback.status === "processing" && (
                                <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-[10.5px]">
                                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" /> 后台处理中
                                </span>
                            )}
                            {playback.status === "ready" && (
                                <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 text-[10.5px]">
                                    ✓ 可播放
                                </span>
                            )}
                            {playback.status === "failed" && (
                                <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 text-[10.5px]">
                                    处理失败
                                </span>
                            )}
                        </div>
                        <div className="font-mono text-[10px] text-neutral-500 break-all">
                            {uploadedPreview.startsWith("r2key:") ? "草稿私有存储(公开 URL 不可用)" : uploadedPreview}
                        </div>
                        {posterPreview && <div className="font-mono text-[10px] text-neutral-500 break-all mt-1">Poster: {posterPreview.startsWith("r2key:") ? "草稿私有存储" : "公开"}</div>}
                        <div className="mt-2 flex gap-3 items-center">
                            <button onClick={removeUploaded} data-testid="video-remove-btn" className="text-red-600 text-xs inline-flex items-center gap-1 hover:underline"><Trash2 className="h-3 w-3" /> 清除</button>
                            {playback.status === "failed" && (
                                <button onClick={retryTranscode} data-testid="video-retry-transcode-btn" className="text-amber-700 text-xs hover:underline">重新处理</button>
                            )}
                        </div>
                    </div>
                )}
                {pendingKey && !uploadedPreview && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs" data-testid="video-pending-key">
                        <div className="font-medium mb-1 text-amber-800">检测到上次上传尚未保存关联</div>
                        <div className="text-amber-700 mb-2">文件已存储在 R2(key:<code className="font-mono text-[10px]">{pendingKey.slice(0,60)}…</code>),点击下面重新保存,不需要重新上传整文件。</div>
                        <button onClick={retryConfirmOnly} data-testid="video-retry-confirm-only" className="inline-flex items-center gap-1 bg-amber-700 text-white rounded-md px-3 py-1 text-xs">仅重新保存</button>
                    </div>
                )}
                <label className="block mb-3">
                    <div className="text-xs font-medium text-neutral-700 mb-1">视频文件</div>
                    <input type="file" accept="video/mp4,video/quicktime,video/webm" onChange={pickVideo} disabled={busy} data-testid="video-file-input"
                           className="block w-full text-sm text-neutral-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border file:border-neutral-300 file:bg-white file:text-sm file:font-medium hover:file:bg-neutral-50 disabled:opacity-50" />
                    {file && <div className="mt-1 text-xs text-neutral-500">{file.name} · {(file.size / 1048576).toFixed(1)} MB</div>}
                </label>
                <label className="block mb-3">
                    <div className="text-xs font-medium text-neutral-700 mb-1">Poster(视频封面图)</div>
                    <input type="file" accept="image/jpeg,image/png,image/webp" onChange={pickPoster} disabled={busy} data-testid="video-poster-input"
                           className="block w-full text-sm text-neutral-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border file:border-neutral-300 file:bg-white file:text-sm file:font-medium hover:file:bg-neutral-50 disabled:opacity-50" />
                    {posterFile && <div className="mt-1 text-xs text-neutral-500">{posterFile.name} · {(posterFile.size / 1048576).toFixed(2)} MB</div>}
                </label>
                {(stage === "signing" || stage === "uploading" || stage === "confirming" || stage === "poster" || stage === "done") && (
                    <div className="mt-2 mb-3" data-testid="video-progress">
                        <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all duration-200 ${stage === "signing" || (stage === "uploading" && !progress.firstByte) ? "bg-amber-400 animate-pulse" : "bg-cafe-olive"}`}
                                style={{ width: `${Math.max(stage === "signing" ? 2 : (stage === "uploading" && !progress.firstByte ? 5 : progress.pct), 0)}%` }}
                            />
                        </div>
                        <div className="text-xs text-neutral-600 mt-1.5 flex items-center justify-between gap-3" data-testid="video-progress-text">
                            <span>
                                {stage === "signing" && "① 正在获取上传地址…"}
                                {stage === "uploading" && !progress.firstByte && "② 等待 R2 响应首个数据包…(约 1–15 秒)"}
                                {stage === "uploading" && progress.firstByte && `② 上传中 · ${progress.pct}%`}
                                {stage === "confirming" && "③ 确认文件中…"}
                                {stage === "poster" && "④ 上传 Poster 中…"}
                                {stage === "done" && "✓ 原片已可播放 · 后台正在生成网页版"}
                            </span>
                            {stage === "uploading" && progress.firstByte && (
                                <span className="text-neutral-500 text-[11px] font-mono">
                                    {(progress.loaded / 1048576).toFixed(1)} / {(progress.total / 1048576).toFixed(1)} MB
                                </span>
                            )}
                        </div>
                    </div>
                )}
                {stage === "cancelled" && (
                    <div className="mb-3 rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700" data-testid="video-cancelled">
                        已取消。可重新选择文件再试。
                    </div>
                )}
                {stage === "error" && errInfo && (
                    <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm" data-testid="video-error">
                        <div className="font-medium text-red-800">{errInfo.title}</div>
                        {errInfo.message && <div className="text-red-700 mt-1 text-[13px] leading-relaxed">{errInfo.message}</div>}
                        {errInfo.hint && <div className="text-red-700/70 mt-1 text-[11px] font-mono">阶段: {errInfo.hint}{errInfo.status ? ` · HTTP ${errInfo.status}` : ""}</div>}
                        {errInfo.canRetryConfirmOnly && pendingKey && (
                            <button onClick={retryConfirmOnly} data-testid="video-error-retry-save" className="mt-2 inline-flex items-center bg-red-700 text-white rounded-md px-3 py-1 text-xs">仅重新保存(不重传)</button>
                        )}
                    </div>
                )}
                <div className="flex gap-2">
                    <button onClick={uploadVideo} disabled={!file || busy} data-testid="video-upload-btn"
                            className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream py-2.5 text-sm font-medium disabled:opacity-50">
                        <Upload className="h-4 w-4" />{uploadButtonLabel}
                    </button>
                    {busy && (
                        <button onClick={() => {
                                // Real cancel: set the ref flag + abort the
                                // XHR. The Promise rejects with kind=CANCELLED
                                // and uploadVideo sets stage="cancelled",
                                // which re-enables the Upload button and
                                // shows the "已取消" banner.
                                abortRef.current.current = true;
                                try { abortRef.current.xhr?.abort(); } catch (_) {}
                            }} data-testid="video-cancel"
                                className="px-3 py-2.5 text-sm rounded-lg border border-neutral-300 hover:bg-neutral-50">取消</button>
                    )}
                </div>
                <div className="mt-5 pt-4 border-t">
                    <div className="text-xs font-medium text-neutral-700 mb-1">兼容选项 · 外部视频链接(YouTube / Vimeo)</div>
                    <div className="flex gap-2">
                        <input value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)}
                               placeholder="https://youtube.com/..." data-testid="video-external-url"
                               className="flex-1 border border-neutral-300 rounded-md px-2.5 py-2 text-sm" />
                        <button onClick={saveExternalOnly} data-testid="video-external-save"
                                className="px-3 py-2 text-sm rounded-md border border-neutral-300 hover:bg-neutral-50">保存链接</button>
                    </div>
                    <div className="text-[10px] text-neutral-500 mt-1">没有直接上传视频时作为展示回退 — 已上传的视频会优先显示。</div>
                </div>
            </div>
        </div>
    );
}

export function DraftPreviewLink({ project, headers }) {
    const open = async () => {
        try {
            await axios.get(`${API}/studio/projects/${project.slug}/preview`, { headers });
            window.open(`/studio/preview/${encodeURIComponent(project.slug)}`, "_blank", "noopener");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "无法打开草稿预览");
        }
    };
    return (
        <button onClick={open} data-testid={`draft-preview-${project.slug}`}
                className="inline-flex items-center gap-1.5 text-xs text-cafe-olive hover:underline">
            <ExternalLink className="h-3.5 w-3.5" /> 草稿预览
        </button>
    );
}

